import React, { useMemo, useState } from "react";
import PropTypes from "prop-types";
import clsx from "clsx";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import TableSortLabel from "@material-ui/core/TableSortLabel";
import DeleteIcon from "@material-ui/icons/Delete";
import A_SecuredAction from "../../../Atoms/Security/A_SecuredAction";
import { pageSecurityActions } from "../../../../../Utils/Helpers/pageSecurity";
import Avatar from "@material-ui/core/Avatar";
import IconAction from "./IconAction";
import StatusTag from "./StatusTag";
import useStyles from "./ProfessionalContacts.styles";

const COLUMNS = [
  { key: "name", label: "Name" },
  { key: "phone", label: "Number" },
  { key: "email", label: "Email Address" },
];

const initialsOf = (name) =>
  (name || "?")
    .split(" ")
    .map((word) => word[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

const IndividualsTable = ({ contacts, onSetMain, onDelete }) => {
  const classes = useStyles();
  const [order, setOrder] = useState({ key: "name", dir: "asc" });

  const sorted = useMemo(() => {
    const list = [...contacts];
    list.sort((a, b) => {
      const av = (a[order.key] || "").toString();
      const bv = (b[order.key] || "").toString();
      const cmp = av.localeCompare(bv, undefined, {
        numeric: true,
        sensitivity: "base",
      });
      return order.dir === "asc" ? cmp : -cmp;
    });
    return list;
  }, [contacts, order]);

  const handleSort = (key) => {
    setOrder((prev) =>
      prev.key === key
        ? { key, dir: prev.dir === "asc" ? "desc" : "asc" }
        : { key, dir: "asc" }
    );
  };

  const deleteButton = (contact) => (
    <A_SecuredAction permission={pageSecurityActions.DELETE}>
      <IconAction
        danger
        title="Delete Individual"
        icon={<DeleteIcon fontSize="small" />}
        onClick={() => onDelete(contact)}
      />
    </A_SecuredAction>
  );

  const mainContactCell = (contact) =>
    contact.isMain ? (
      <StatusTag tone="valid">Main Contact</StatusTag>
    ) : (
      <button
        type="button"
        className={classes.linkAction}
        onClick={() => onSetMain(contact.id)}
      >
        Set Main
      </button>
    );

  if (contacts.length <= 1) {
    const contact = contacts[0];
    if (!contact) return null;
    return (
      <div className={classes.rowItem}>
        <Avatar className={classes.avatar}>{initialsOf(contact.name)}</Avatar>
        <div className={classes.rowGrow}>
          <span className={classes.cellStrong}>{contact.name}</span>
          {contact.role ? ` · ${contact.role}` : ""}
          <div className={classes.subtle}>
            {contact.email} · {contact.phone}
          </div>
        </div>
        {mainContactCell(contact)}
        {deleteButton(contact)}
      </div>
    );
  }

  return (
    <div className={classes.tableContainer}>
      <Table size="small" className={classes.table}>
        <TableHead className={classes.tableHead}>
          <TableRow>
            {COLUMNS.map((col) => (
              <TableCell
                key={col.key}
                sortDirection={order.key === col.key ? order.dir : false}
              >
                <TableSortLabel
                  className={classes.sortLabel}
                  active={order.key === col.key}
                  direction={order.key === col.key ? order.dir : "asc"}
                  onClick={() => handleSort(col.key)}
                >
                  {col.label}
                </TableSortLabel>
              </TableCell>
            ))}
            <TableCell />
          </TableRow>
        </TableHead>
        <TableBody className={classes.tableBody}>
          {sorted.map((contact) => (
            <TableRow key={contact.id}>
              <TableCell>
                <span className={classes.cellStrong}>{contact.name}</span>
                {contact.role && (
                  <div className={classes.subtle}>{contact.role}</div>
                )}
              </TableCell>
              <TableCell className={classes.cellNowrap}>
                {contact.phone}
              </TableCell>
              <TableCell>{contact.email}</TableCell>
              <TableCell
                align="right"
                className={clsx(classes.cellActions, classes.cellNowrap)}
              >
                {mainContactCell(contact)}
                {deleteButton(contact)}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

IndividualsTable.propTypes = {
  contacts: PropTypes.array.isRequired,
  onSetMain: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
};

export default IndividualsTable;
